import { ethers, utils } from 'ethers'

import { Parameters } from '../interfaces'
import Web3 from 'web3'

export const isArrayType = (type: string) => {
    return type === 'tuple' || type.includes('[]')
}

export const isUintType = (type: string) => {
    return type.toLowerCase().includes('uint')
}

export const isBytesType = (type: string) => {
    return type.toLowerCase().includes('bytes')
}

export const isAddressType = (type: string) => {
    return type.toLowerCase().includes('address')
}

export const isBooleanType = (type: string) => {
    return type.toLowerCase().includes('bool')
}

export const getPlaceholder = (type: string) => {
    const isArray = isArrayType(type)
    const isUint = isUintType(type)
    const isBytes = isBytesType(type)
    const isBoolean = isBooleanType(type)
    const isAddress = isAddressType(type)

    let exampleText = ''

    if (isArray) {
        if (isUint || isBytes) {
            exampleText = '[0,1,2]'
        } else if (isBoolean) {
            exampleText = '[true, false, false]'
        } else if (isAddress) {
            exampleText = `["0x11...11", "0x21...21", "0x31...31"]`
        } else {
            exampleText = `["str0", "str1", "str2"]`
        }
    } else {
        if (isUint || isBytes) {
            exampleText = '111222333'
        } else if (isBoolean) {
            exampleText = 'false'
        } else if (isAddress) {
            exampleText = `0x11...11`
        } else {
            exampleText = `str0`
        }
    }

    return `Example: ${exampleText}`
}

enum VALIDATE_ERRORS {
    NOT_ARRAY = 'Invalid input: Value is not an array.',
    NOT_BOOLEAN = 'Invalid input: bool must be true or false',
    NOT_ADDRESS = 'Invalid input: value is not an address',
}

type Validator = (value: string) => string | null

const validateArray = (value: string, itemValidator: Validator) => {
    try {
        const parsedValue = JSON.parse(value)
        if (!Array.isArray(parsedValue)) return VALIDATE_ERRORS.NOT_ARRAY
        let message: string | null = null
        parsedValue.forEach((item) => {
            if (message) return
            if (typeof item !== 'string') item = item.toString()
            message = itemValidator(item)
        })

        return message
    } catch (e: any) {
        return e.message
    }
}

const validators: { [x: string]: Validator } = {
    address: (value: string) => (utils.isAddress(value) ? null : VALIDATE_ERRORS.NOT_ADDRESS),
    'address[]': (value: string) => validateArray(value, validators.address),
    bool: (value: string) => {
        if (!['true', 'false'].includes(value)) {
            return VALIDATE_ERRORS.NOT_BOOLEAN
        }
        return null
    },
    'bool[]': (value: string) => validateArray(value, validators.bool),
}

const parsers: { [x: string]: (value: string) => any } = {
    bool: (value: string): boolean => value === 'true',
}

const abiCoder = new utils.AbiCoder()

export const encode = (parameters: Parameters) => {
    const inputsLength = parameters.inputs.length
    let types: string[] = []
    let inputs: any[] = []
    let errors: string[] = []
    let valid = true
    let encoded = ''
    let emptyLines = parameters.inputs.filter((input) => !input.value).length

    if (emptyLines === inputsLength) {
        valid = false
        return {
            errors,
            encoded,
        }
    }

    parameters.inputs.forEach((item, index) => {
        const { type, value, placeholder } = item
        if (type === 'tuple' && placeholder) {
            types.push(`tuple(${placeholder})`)
        } else {
            types.push(type)
        }

        const validator: Validator = validators[type] || (() => '')
        const isArray = isArrayType(type)
        const parser: any = parsers[type] || ((v: any) => v)
        let errorMessage = validator(value)
        if (!errorMessage && isArray) {
            errorMessage = validateArray(value, () => '')
        }
        if (errorMessage) {
            valid = false
            errors[index] = errorMessage
            return
        } else {
            if (isArray && value) {
                inputs.push(JSON.parse(value))
            } else {
                inputs.push(parser(value))
            }
        }
        try {
            abiCoder.encode(types, inputs)
            errors[index] = ''
        } catch (err: any) {
            valid = false
            errors[index] = `Invalid input: ${err.message}`
        }
    })

    if (valid) {
        if (parameters.type !== 'constructor') {
            const sig = parameters.funcName + '(' + types.join(',') + ')'
            encoded += utils.hexDataSlice(utils.id(sig), 0, 4).slice(2)
        }
        encoded += abiCoder.encode(types, inputs).slice(2)
        return {
            errors,
            encoded,
        }
    } else {
        return {
            errors,
            encoded,
        }
    }
}

const orderParsedFunction = (parsedFunctions: any) => {
    return parsedFunctions.sort((func1: any, func2: any) => {
        if (func1.stateMutability < func2.stateMutability) return 1
        if (func1.stateMutability > func2.stateMutability) return -1

        return 0
    })
}

export const parse = (abi: any) => {
    const functions: any[] = orderParsedFunction(JSON.parse(abi))

    let parsedFunctions: { [x: string]: any } = {}
    const uniqueFunc = new Set()
    functions.forEach((func) => {
        if (func.type !== 'event' && typeof func.inputs !== 'undefined') {
            if (func.type === 'constructor') {
                parsedFunctions[func.type] = func
            } else {
                const uniqueFunctionName = !uniqueFunc.has(func.name)
                    ? func.name
                    : `${func.name}_${Math.floor(Math.random() * 100)}`
                parsedFunctions[uniqueFunctionName] = func
                uniqueFunc.add(uniqueFunctionName)
            }
        }
    })

    return parsedFunctions
}
export const isAddress = (value: string | null | undefined) => {
    if (!value) return false
    return ethers.utils.isAddress(value)
}

export const isJSON = (jsonString: string) => {
    try {
        return JSON.parse(jsonString) && !!jsonString
    } catch (e) {
        return false
    }
}

export const isValidTxHash = (txHash: string | null | undefined) => {
    return txHash && /^0x([A-Fa-f0-9]{64})$/.test(txHash)
}

export const recursionCalls = (container: any, calls: any, error: any[] = []) => {
    if (container && container?.revertReason) {
        error.push({
            reason: container.revertReason,
            from: container.from,
            to: container.to,
            gas: container.gas,
            gasUsed: container.gasUsed,
            input: container.input,
            output: container.output,
        })
    }
    if (Array.isArray(calls) && calls.length > 0) {
        for (const i of calls) {
            recursionCalls(i, i.calls, error)
        }
    }
    return error
}

export function decodeLog(logs: any, abi: any) {
    const iface = new ethers.utils.Interface(abi)
    let results: any = []
    logs.forEach((log: any) => {
        const logDesc = iface.parseLog(log)
        results.push({
            name: logDesc?.name,
            args: logDesc?.args,
        })
    })
    return results
}

// helper: get provider-suggested priority fee (wei)
export async function getPriorityFee(web3: Web3) {
    const prov: any = web3.currentProvider ?? (window as any).ethereum
    try {
        // modern providers expose .request
        if (prov?.request) {
            const hex = await prov.request({ method: 'eth_maxPriorityFeePerGas' })
            return web3.utils.toBN(hex) // wei
        }

        // fallback: add a custom web3 method and call it
        web3.eth.extend({
            methods: [{ name: 'getMaxPriorityFeePerGas', call: 'eth_maxPriorityFeePerGas' }],
        })
        const hex = await (web3.eth as any).getMaxPriorityFeePerGas()
        return web3.utils.toBN(hex)
    } catch (error) {
        console.log('🚀 ~ getPriorityFee ~ error:', error)
        return null // provider doesn't support it
    }
}

export async function getPriorityFromHistory(web3: Web3) {
    if (!web3.eth.getFeeHistory) return null

    // last 5 blocks, take 50th percentile reward (priority fee)
    const h = await web3.eth.getFeeHistory(5, 'latest', [50])
    // h.reward is an array of arrays: [[p50],[p50],...]
    const tips = h.reward.map((arr: string[]) => web3.utils.toBN(arr[0]))
    const avg = tips.reduce((a: any, b: any) => a.add(b), web3.utils.toBN(0)).divn(tips.length || 1)
    return avg // wei
}
